"use client";

import { useState } from "react";
import { SelectField } from "@/components/tool-kit/primitives";
import { CARD, TABLE, TD, TH } from "@/components/tool-kit/styles";
import {
  DiagramDownload,
  useDiagramRef,
} from "@/components/tools/geometry/diagram";
import {
  type DecimalPlaces,
  type Format,
  formatMeasure,
} from "@/components/tools/geometry/format";
import {
  MeasurementFields,
  type UnitControlStrings,
  UnitControls,
} from "@/components/tools/geometry/measurement-fields";
import { SolidDiagram } from "@/components/tools/geometry/solid-diagrams";
import { t } from "@/lib/i18n/interpolate";
import { type ExactFormat, formatExact } from "@/lib/tool-kit/geometry/exact";
import type { UnitSetup } from "@/lib/tool-kit/geometry/measurements";
import {
  SOLID_DEFAULTS,
  type SolidOutcome,
} from "@/lib/tool-kit/geometry/solid-input";
import {
  SOLID_FIELDS,
  SOLID_IDS,
  type SolidFieldId,
  type SolidId,
  type SolidMetric,
  type SolidMetricKey,
} from "@/lib/tool-kit/geometry/solids";
import { type LengthUnitId, unitSymbol } from "@/lib/tool-kit/geometry/units";

/** The strings the shared solid inputs and figure need; each tool's strings include them. */
export interface SolidInputStrings extends UnitControlStrings {
  solidLabel: string;
  solids: Record<SolidId, string>;
  fields: Record<SolidFieldId, string>;
  unitAria: string;
  figureTitle: string;
  downloadFigure: string;
  slantLabel: string;
}

export interface SolidInputState {
  solid: SolidId;
  setSolid: (next: SolidId) => void;
  values: Record<string, string>;
  setValue: (id: string, value: string) => void;
  setup: UnitSetup;
  setSetup: (next: UnitSetup) => void;
  places: DecimalPlaces;
  setPlaces: (next: DecimalPlaces) => void;
}

export function useSolidInputs(initial: SolidId): SolidInputState {
  const [solid, setSolid] = useState<SolidId>(initial);
  const [all, setAll] =
    useState<Record<string, Record<string, string>>>(SOLID_DEFAULTS);
  const [setup, setSetup] = useState<UnitSetup>({
    mixed: false,
    unit: "cm",
    units: {},
    resultUnit: "cm",
  });
  const [places, setPlaces] = useState<DecimalPlaces>("2");
  return {
    solid,
    setSolid,
    values: all[solid] ?? {},
    setValue: (id, value) =>
      setAll({ ...all, [solid]: { ...all[solid], [id]: value } }),
    setup,
    setSetup,
    places,
    setPlaces,
  };
}

export function SolidInputPanel({
  idPrefix,
  state,
  strings,
}: {
  idPrefix: string;
  state: SolidInputState;
  strings: SolidInputStrings;
}) {
  const { solid, setup } = state;
  const fields = SOLID_FIELDS[solid];
  const shownUnits = Object.fromEntries(
    fields.map((id) => [id, setup.units[id] ?? setup.unit])
  ) as Record<string, LengthUnitId>;
  return (
    <div className="space-y-4">
      <SelectField
        id={`${idPrefix}-solid`}
        label={strings.solidLabel}
        onChange={state.setSolid}
        options={SOLID_IDS.map((id) => ({
          value: id,
          label: strings.solids[id],
        }))}
        value={solid}
      />
      <MeasurementFields
        fields={fields.map((id) => ({ id, label: strings.fields[id] }))}
        idPrefix={idPrefix}
        mixed={setup.mixed}
        onUnit={(id, unit) =>
          state.setSetup({ ...setup, units: { ...setup.units, [id]: unit } })
        }
        onValue={state.setValue}
        unitAria={strings.unitAria}
        units={shownUnits}
        values={state.values}
      />
      <UnitControls
        idPrefix={idPrefix}
        onPlaces={state.setPlaces}
        onSetup={state.setSetup}
        places={state.places}
        setup={setup}
        strings={strings}
      />
    </div>
  );
}

/** The labelled figure of a solved solid, with its download button. */
export function SolidFigure({
  outcome,
  solid,
  strings,
  slant,
  fileName,
}: {
  outcome: Extract<SolidOutcome, { ok: true }>;
  solid: SolidId;
  strings: SolidInputStrings;
  /** The slant height text to draw, when the figure should show one. */
  slant?: string;
  fileName: string;
}) {
  const svgRef = useDiagramRef();
  return (
    <div className="space-y-2">
      <SolidDiagram
        labels={outcome.read.labels}
        slantLabel={slant}
        solid={solid}
        svgRef={svgRef}
        title={t(strings.figureTitle, { solid: strings.solids[solid] })}
        values={outcome.read.numbers}
      />
      <DiagramDownload
        fileName={fileName}
        label={strings.downloadFigure}
        svgRef={svgRef}
      />
    </div>
  );
}

export function SolidMetricsTable({
  metrics,
  unit,
  labels,
  headers,
  fmt,
  places,
  format,
}: {
  metrics: SolidMetric[];
  unit: LengthUnitId;
  labels: Record<SolidMetricKey, string>;
  headers: { measurement: string; exact: string; decimal: string };
  fmt: Format;
  places: number;
  format: ExactFormat;
}) {
  return (
    <div className={`${CARD} overflow-x-auto`}>
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {headers.measurement}
            </th>
            <th className={TH} scope="col">
              {headers.exact}
            </th>
            <th className={TH} scope="col">
              {headers.decimal}
            </th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => {
            const suffix = ` ${unitSymbol(unit, metric.kind === "area" ? 2 : 1)}`;
            return (
              <tr key={metric.key}>
                <th className={`${TD} font-normal`} scope="row">
                  {labels[metric.key]}
                </th>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {formatExact(metric.exact, format)}
                  {suffix}
                </td>
                <td className="border-border border-b px-3 py-2 font-mono text-foreground">
                  {formatMeasure(fmt, metric.value, places)}
                  {suffix}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
